import { SupabaseClient } from '@supabase/supabase-js';

export interface DatabaseResult<T> {
  data: T | null;
  error: any;
}

export interface DatabaseArrayResult<T> {
  data: T[] | null;
  error: any;
}

/**
 * Base repository class that provides common CRUD operations
 * Preserves all existing business logic patterns while reducing duplication
 */
export class BaseRepository {
  constructor(protected supabase: SupabaseClient<any, 'public', any>) {}

  /**
   * Generic function to get data by ID from any table
   * Preserves exact logic from getDataByIdFromTableName
   */
  protected async getDataById<T>(
    tableName: string,
    idName: string,
    idValue: string,
    excludeDeletedColumn = false,
  ): Promise<DatabaseArrayResult<T>> {
    try {
      if (excludeDeletedColumn) {
        const { data, error } = await this.supabase
          .from(tableName)
          .select('*')
          .eq(idName, idValue);
        return { data, error };
      } else {
        const { data, error } = await this.supabase
          .from(tableName)
          .select('*')
          .eq(idName, idValue)
          .eq('deleted', false);
        return { data, error };
      }
    } catch (error) {
      console.error(`Error getting data from ${tableName}:`, error);
      return { data: null, error };
    }
  }

  /**
   * Generic function to insert data into any table
   * Preserves exact logic from insertDataToTableName
   */
  protected async insertData<T>(
    tableName: string,
    tableContent: any,
  ): Promise<DatabaseArrayResult<T>> {
    try {
      const { data, error } = await this.supabase
        .from(tableName)
        .insert([tableContent])
        .select();
      return { data, error };
    } catch (error) {
      console.error('Error inserting data:', error);
      return { data: null, error };
    }
  }

  /**
   * Generic function to update data by ID in any table
   * Preserves exact logic from updateDataByIdFromTableName
   */
  protected async updateDataById<T>(
    tableName: string,
    idName: string,
    idValue: string,
    tableContent: any,
  ): Promise<DatabaseArrayResult<T>> {
    try {
      const { data, error } = await this.supabase
        .from(tableName)
        .update([tableContent])
        .eq(idName, idValue);
      return { data, error };
    } catch (error) {
      console.error('Error updating data:', error);
      return { data: null, error };
    }
  }

  /**
   * Generic function to soft delete data by ID
   * Preserves exact logic from removeDataByIdFromTableName
   */
  protected async softDeleteById(
    tableName: string,
    idName: string,
    idValue: string,
  ): Promise<void> {
    try {
      await this.supabase
        .from(tableName)
        .update({ deleted: true })
        .eq(idName, idValue);
    } catch (error) {
      console.error('Error deleting data:', error);
    }
  }

  /**
   * Generic function to get all data from a table
   */
  protected async getAllData<T>(
    tableName: string,
    excludeDeleted = true,
  ): Promise<DatabaseArrayResult<T>> {
    try {
      let query = this.supabase.from(tableName).select('*');

      if (excludeDeleted) {
        query = query.eq('deleted', false);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      console.error(`Error getting all data from ${tableName}:`, error);
      return { data: null, error };
    }
  }

  /**
   * Generic function to get data with multiple conditions
   */
  protected async getDataByConditions<T>(
    tableName: string,
    conditions: Record<string, any>,
    excludeDeleted = true,
  ): Promise<DatabaseArrayResult<T>> {
    try {
      let query = this.supabase.from(tableName).select('*');

      // Apply all conditions
      Object.entries(conditions).forEach(([key, value]) => {
        query = query.eq(key, value);
      });

      if (excludeDeleted) {
        query = query.eq('deleted', false);
      }

      const { data, error } = await query;
      return { data, error };
    } catch (error) {
      console.error(`Error getting data from ${tableName}:`, error);
      return { data: null, error };
    }
  }

  /**
   * Public method to access supabase client
   */
  get supabaseClient() {
    return this.supabase;
  }

  /**
   * Public getAllData method
   */
  public async getAllDataPublic<T>(
    tableName: string,
    excludeDeleted = true,
  ): Promise<DatabaseArrayResult<T>> {
    return this.getAllData<T>(tableName, excludeDeleted);
  }

  /**
   * Public softDeleteById method
   */
  public async softDeleteByIdPublic(
    tableName: string,
    idName: string,
    idValue: string,
  ): Promise<void> {
    return this.softDeleteById(tableName, idName, idValue);
  }
}
